import React, { useState, useEffect } from "react";
import BackButton from "../../components/buttons/BackButton";
import ArtworkImageLoader from "../../components/ArtworkImageLoader";
import { useStore } from "../../store/store";
import { ArtworkWithTexture } from "../../types";
import { useLocation, useSearch } from "wouter";
import { animated, useTransition } from "@react-spring/web";

interface Selected {
  name: string;
  type: string;
  uuid: string;
}

const SearchView: React.FC = () => {
  const [, navigate] = useLocation();
  const search = useSearch(); // Using useSearch to access query parameters
  const { setArtworkInFront } = useStore();
  const [searchResults, setSearchResults] = useState<ArtworkWithTexture[]>([]);
  const [selected, setSelected] = useState<Selected>({
    name: "",
    type: "",
    uuid: "",
  });

  // Function to handle navigation back
  const back = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate("/collection");
    }
  };

  // Function to fetch search results based on selected.type and selected.uuid
  const getResult = async (type: string, uuid: string) => {
    const fetchUrl = `https://admin.collectieconnectie.nl/api/v1/${type}/${uuid}/artworks`;
    try {
      const response = await fetch(fetchUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setSearchResults(data.artworks.data);
    } catch (error) {
      console.error("Error fetching search results:", error);
      // TODO: Save last search to localStorage
      navigate("/collection");
    }
  };

  // Compute the icon class based on selected.type
  const typeIconClass =
    selected.type === "author" ? "d-user-profile-circle" : "d-network-policy";

  // Manage overflow-hidden class on document.body
  useEffect(() => {
    // Add overflow-hidden when component mounts
    document.body.classList.add("overflow-hidden");
    return () => {
      // Remove overflow-hidden when component unmounts
      document.body.classList.remove("overflow-hidden");
    };
  }, []);

  // Initialize selected state and fetch results when search params change
  useEffect(() => {
    const params = new URLSearchParams(search);
    const name = params.get("name")
      ? decodeURIComponent(params.get("name")!)
      : "";
    const type = params.get("type") || "";
    const uuid = params.get("uuid") || "";

    if (type && uuid && name) {
      const newSelected: Selected = { name, type, uuid };
      setSelected(newSelected);
      getResult(type, uuid);
    } else {
      // If required params are missing, navigate back to collection
      navigate("/collection");
    }
  }, [search, navigate]);

  // Set artwork in front (existing functionality)
  useEffect(() => {
    setArtworkInFront(true);
  }, [setArtworkInFront]);

  const transitions = useTransition(true, {
    from: {
      opacity: 0,
      backdropFilter: "blur(0px)",
      WebkitBackdropFilter: "blur(0px)",
    },
    enter: {
      opacity: 1,
      backdropFilter: "blur(30px)",
      WebkitBackdropFilter: "blur(30px)",
    },
    leave: {
      backdropFilter: "blur(0px)",
      opacity: 0,
      WebkitBackdropFilter: "blur(0px)",
    },
    config: { tension: 280, friction: 60 },
  });

  return transitions(
    (styles, item) =>
      item && (
        <animated.div
          style={{
            ...styles,
          }}
          className={
            "z-20 min-h-screen md:overflow-hidden md:h-full w-full absolute top-0 left-0 bg-transparent overflow-y-scroll no-scrollbar"
          }
        >
          <div id="SearchResult" className="bg-brand">
            {/* Actions */}
            <div className="pt-8 px-4 absolute z-10">
              <BackButton onClick={back} />
            </div>

            {/* Title Mobile */}
            <div className="px-4 pt-4 md:hidden">
              <h1 className="text-3xl text-white">
                {selected.name || "Geschiedenis"}
              </h1>
              <span className="inline-block text-gray-400">
                <span>
                  {selected.type ? `${selected.type} objecten` : "objecten"}
                </span>
              </span>
              <span className="block text-sm text-gray-400 mt-2">
                {searchResults.length} objecten
              </span>
            </div>

            {/* Artwork Listing */}
            <div className="mx-auto px-4 py-4 md:py-16 relative no-scrollbar md:overflow-scroll md:min-h-screen md:h-screen md:pr-56 md:pl-24 xl:container">
              {/* Title Desktop */}
              <h1 className="text-3xl text-white hidden md:block">
                {selected.name || "Geschiedenis"}
              </h1>
              <span className="text-sm md:inline-flex items-center text-gray-400 mr-3">
                {selected.type && (
                  <>
                    <span className={`d-icon mr-2 ${typeIconClass}`}></span>
                    <span>{selected.type}</span>
                  </>
                )}
              </span>
              <span className="text-sm md:inline-flex items-center text-gray-400 ">
                {searchResults.length} objecten
              </span>
              {/* Artwork List */}
              <ul className="sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 md:gap-x-4 gap-y-4 pt-8">
                {searchResults.map((artwork) => (
                  <li
                    key={artwork.uuid}
                    className={`relative pb-5 select-none`}
                  >
                    <div className="group block w-full aspect-none relative">
                      <ArtworkImageLoader
                        className="w-full object-contain object-left max-h-[28rem] pointer-events-none"
                        artwork={artwork}
                        lowResImageOnly={true}
                      />
                    </div>
                    <p className="mt-3 block text-lg leading-none text-white truncate pointer-events-none">
                      {artwork.title}
                    </p>
                    <p className="block text-sm font-bold text-white pointer-events-none">
                      {artwork.author.name}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </animated.div>
      ),
  );
};

export default SearchView;
